/**
 * Rezept von einer Webseite holen — der Server liest die Seite (mit Schutz vor
 * Abrufen ins interne Netz), die App zeigt eine Vorschau und legt an.
 */

import type { Nutrients } from '@kraftwerk/shared';
import { ApiError, apiFetch } from '../api.js';

export interface ImportedNutrition {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  nutrients: Nutrients;
}

export interface ImportPreview {
  recipe: {
    name: string;
    portions: number | null;
    ingredients: string[];
    steps: string[];
    /** Nährwerte je Portion von der Seite (16.10.2026); ältere Server: fehlt. */
    nutrition?: ImportedNutrition | null;
  };
  sourceUrl: string;
  image: { base64: string; contentType: string } | null;
}

export type ImportProblem =
  'blocked' | 'noRecipe' | 'unreachable' | 'refused' | 'tooMany' | 'offline';

export async function fetchImport(
  url: string,
): Promise<{ ok: true; preview: ImportPreview } | { ok: false; problem: ImportProblem }> {
  try {
    const preview = await apiFetch<ImportPreview>('/recipes/import', {
      method: 'POST',
      body: JSON.stringify({ url: withScheme(url) }),
    });
    return { ok: true, preview };
  } catch (error) {
    if (!(error instanceof ApiError)) return { ok: false, problem: 'offline' };
    if (error.status === 429) return { ok: false, problem: 'tooMany' };
    if (error.status === 422) return { ok: false, problem: 'noRecipe' };
    if (error.status === 400) return { ok: false, problem: 'blocked' };
    if (error.reason === 'refused') return { ok: false, problem: 'refused' };
    return { ok: false, problem: 'unreachable' };
  }
}

/** „chefkoch.de/…" ohne Schema ist auf dem Telefon der Normalfall. */
export function withScheme(url: string): string {
  const trimmed = url.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function imageBlob(image: ImportPreview['image']): Blob | null {
  if (!image) return null;
  const bytes = Uint8Array.from(atob(image.base64), (char) => char.charCodeAt(0));
  return new Blob([bytes], { type: image.contentType });
}
